import Grid from "@mui/material/Grid";
import greyDialogTitleSx from '../Dialog/greyDialogTitleSx';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { IOptions } from "../FormFields/CustomSelect";
import { inviteEmployee, fetchEmployeesWithoutAccess } from '../../store/employeeStore/employeeThunk';
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import LoadingButton from "@mui/lab/LoadingButton";
import { ApiStatus } from "../../utils/api.types";
import { fetchRoles } from "../../store/rolesThunk";

import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {
  Avatar, Chip, FormControl, FormControlLabel, FormHelperText, FormLabel, IconButton, ListItem, ListItemAvatar,
  ListItemText, Radio, RadioGroup
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';

// Form Input interface
interface IEmployeeInvite {
  employeeId: string;
  role: string;
  welcomeMessage?: string;
};

export interface IDialogProps {
  title: string;
  open: boolean;
  onClose: () => void;
}

const schema = yup.object().shape({
  employeeId: yup.string().required('Employee is required.'),
  role: yup.string().required('Role is required.'),
  welcomeMessage: yup.string().optional(),
});

export default function EmployeeInviteDialog(val: IDialogProps) {
  const dispatch = useAppDispatch();
  const [employeeOptions, setEmployeeOptions] = useState<any[]>([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [userRoles, setUserRoles] = useState<IOptions[]>([]);
  const { status } = useAppSelector((state) => state.employees);
  const [selectedEmployee, setSelectedEmployee] = useState<any>(null);

  useEffect(() => {
    const getRoles = async () => {
      const data: any = await dispatch(fetchRoles());
      const roles = Array.isArray(data?.payload) ? data.payload : [];
      setUserRoles(roles.map((c: any) => {
        return { label: c.role.charAt(0).toUpperCase() + c.role.slice(1), value: c._id }
      }));
    }
    getRoles();
  }, [dispatch]);

  useEffect(() => {
    const getEmployeesWithoutAccess = async () => {
      if (!val.open) return;
      setLoadingEmployees(true);
      const data: any = await dispatch(fetchEmployeesWithoutAccess());
      if (data.payload && data.payload.success) {
        setEmployeeOptions(data.payload.data.map((emp: any) => ({
          ...emp,
          label: `${emp.name} (${emp.empId})`,
          searchText: `${emp.name} ${emp.empId} ${emp.email || ''} ${emp.designation || ''}`
        })));
      }
      setLoadingEmployees(false);
    };
    getEmployeesWithoutAccess();
  }, [dispatch, val.open]);

  const methods = useForm<IEmployeeInvite>({
    resolver: yupResolver(schema),
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: { employeeId: '', role: '', welcomeMessage: '' },
  });
  const { control } = methods;

  const onSubmit = async (data: IEmployeeInvite) => {
    try {
      const res = await dispatch(inviteEmployee({
        employeeId: data.employeeId,
        roleId: data.role,
        welcomeMessage: data.welcomeMessage || ''
      })).unwrap();
      toast.success(res?.message || "Invitation sent successfully");
      handleClose();
    } catch (error: any) {
      toast.error(error?.message || "Failed to send invitation");
    }
  };

  const resetForm = () => {
    setSelectedEmployee(null);
    methods.reset({ employeeId: '', role: '', welcomeMessage: '' });
  };

  const handleClose = () => {
    resetForm();
    val.onClose();
  };

  // Show at most 20 matches; typing narrows the list to reach the rest
  const filterEmployees = (options: any[], { inputValue }: any) => {
    return options.filter(option =>
      option.searchText.toLowerCase().includes(inputValue.toLowerCase())
    ).slice(0, 20);
  };

  const noEmployees = !loadingEmployees && employeeOptions.length === 0;

  return (
    <Dialog open={val.open} onClose={handleClose} fullWidth maxWidth="sm">
      <FormProvider {...methods}>
        <DialogTitle sx={greyDialogTitleSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h5">Employee Invitation</Typography>
              <Typography variant="body2">Give an employee login access to this business</Typography>
            </Box>
            <IconButton size="small" onClick={handleClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          {noEmployees ? (
            <Box sx={{ mt: 3, p: 3, bgcolor: '#f5f6f8', border: '1px solid #e4e4e4', borderRadius: '8px', textAlign: 'center' }}>
              <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#1a1a1a' }}>No employees to invite</Typography>
              <Typography sx={{ fontSize: '13px', color: '#6b7280', mt: 0.5 }}>
                All employees already have user access. Add a new employee first to invite them.
              </Typography>
            </Box>
          ) : (
            <Grid container rowSpacing={3} sx={{ pt: 3 }}>
              <Grid item xs={12}>
                <Controller
                  name='employeeId'
                  control={control}
                  render={({ field, fieldState }) => (
                    <Autocomplete
                      value={selectedEmployee}
                      onChange={(_event, newValue: any) => {
                        setSelectedEmployee(newValue);
                        field.onChange(newValue?._id || '');
                      }}
                      onBlur={field.onBlur}
                      filterOptions={filterEmployees}
                      getOptionLabel={(option: any) => option.label || ''}
                      isOptionEqualToValue={(option: any, value: any) => option._id === value?._id}
                      options={employeeOptions}
                      loading={loadingEmployees}
                      renderOption={(props, option: any) => (
                        <ListItem {...props} key={option._id} dense>
                          <ListItemAvatar>
                            <Avatar>{option.name?.charAt(0)?.toUpperCase()}</Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <span>{option.name}</span>
                                <Chip label={option.empId} size="small" variant="outlined" sx={{ height: 20, fontSize: '11px' }} />
                              </Box>
                            }
                            secondary={[option.designation, option.department?.name, option.email].filter(Boolean).join(' • ')}
                          />
                        </ListItem>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Search Employee"
                          placeholder="Type a name, employee ID or email"
                          variant="standard"
                          required
                          autoFocus
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingEmployees ? <CircularProgress color="inherit" size={18} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            )
                          }}
                        />
                      )}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name='role'
                  control={control}
                  render={({ field, fieldState }) => (
                    <FormControl error={!!fieldState.error} required fullWidth>
                      <FormLabel sx={{ fontSize: '0.875rem', mb: 0.5 }}>Role</FormLabel>
                      {/* Inline radios instead of a Select so the options stay inside the dialog */}
                      <RadioGroup {...field}>
                        <Grid container>
                          {userRoles.map(role => (
                            <Grid item xs={6} sm={4} key={role.value}>
                              <FormControlLabel
                                value={role.value}
                                control={<Radio size="small" />}
                                label={role.label}
                                sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
                              />
                            </Grid>
                          ))}
                        </Grid>
                      </RadioGroup>
                      {fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name='welcomeMessage'
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Welcome Message (Optional)"
                      variant="standard"
                      multiline
                      minRows={2}
                      fullWidth
                      placeholder="Add a personal welcome message for the employee..."
                    />
                  )}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>

        <DialogActions>
          <Button variant='outlined' onClick={handleClose}>
            Cancel
          </Button>
          <LoadingButton
            onClick={methods.handleSubmit(onSubmit)}
            loading={status === ApiStatus.LOADING}
            variant="contained"
            disabled={noEmployees}
          >
            Send Invite
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}
